import dayjs from 'dayjs';
import { InboundProcess } from '@features/supplyChain/types';
import { PlusIcon, CheckIcon } from '@heroicons/react/24/outline';
import {
  FulfilmentStage,
  StageSummaryGrid,
  terminalTone,
  withMonotonicCompletion,
} from './FulfilmentStageTracker';
import { GoodsReceivedNoteSummary } from './GoodsReceivedNoteSummary';

export interface InboundStageViews {
  onViewShipment: () => void;
  onViewGoodsReceivedNote: () => void;
}

export interface InboundStageActions {
  onCreateDraftGoodsReceivedNote?: () => void;
  onSignOffGoodsReceivedNote?: () => void;
}

const formatZoned = (
  value?: { dateTime: string; timeZone: string } | null
): string =>
  value ? dayjs.utc(value.dateTime).tz(value.timeZone).format('DD/MM/YY') : '-';

export function buildInboundStages(
  process: InboundProcess,
  views: InboundStageViews,
  actions: InboundStageActions = {}
): FulfilmentStage[] {
  const { shipmentNote } = process;
  const { goodsReceivedNote } = process;

  const totalItems =
    shipmentNote?.skus.reduce((sum, sku) => sum + sku.skuQuantity, 0) ?? 0;
  const totalReceived = goodsReceivedNote?.skus.reduce(
    (sum, sku) => sum + sku.receivedSkuQuantity,
    0
  );
  const discrepancyAmount = totalItems - (totalReceived ?? 0);
  const isDiscrepancy =
    totalReceived !== undefined && totalReceived !== totalItems;
  const isNegativeDiscrepancy = discrepancyAmount > 0;
  const isRejected = process.status === 'Rejected';

  return withMonotonicCompletion([
    {
      key: 'order',
      pendingLabel: 'Order Accepted',
      doneLabel: 'Order Accepted',
      isComplete: true,
      summary: (
        <StageSummaryGrid
          rows={[
            { value: shipmentNote?.supplier.name ?? '-', label: 'Supplier' },
            {
              value: formatZoned(shipmentNote?.estimatedTimeOfArrival),
              label: 'Est. Arrival',
            },
            {
              value: shipmentNote?.shipmentNoteNumber ?? '-',
              label: 'Order Ref',
            },
            { value: totalItems, label: 'Ordered Qty' },
          ]}
        />
      ),
    },
    {
      key: 'shipment',
      pendingLabel: 'Confirm Arrival',
      doneLabel: 'Shipment Arrived',
      isComplete: !!goodsReceivedNote,
      summary: (
        <StageSummaryGrid
          rows={[
            {
              value: shipmentNote?.shipmentNoteNumber ?? '-',
              label: 'Shipment Note #',
            },
            { value: shipmentNote?.supplier.name ?? '-', label: 'Supplier' },
            {
              value: formatZoned(shipmentNote?.estimatedTimeOfArrival),
              label: 'Est. Arrival',
            },
            { value: shipmentNote?.skus.length ?? 0, label: 'SKUs' },
          ]}
        />
      ),
      view: { label: 'View Shipment Note', onView: views.onViewShipment },
      action: actions.onCreateDraftGoodsReceivedNote
        ? {
            label: 'Create Draft Goods Received Note',
            onAct: actions.onCreateDraftGoodsReceivedNote,
            startIcon: <PlusIcon className='w-4 h-4' />,
          }
        : undefined,
    },
    {
      key: 'grn',
      pendingLabel: 'Confirm Goods Received Note',
      doneLabel: isRejected ? 'GRN Rejected' : 'GRN Confirmed',
      isComplete: !!goodsReceivedNote?.finalisedAt,
      doneTone: terminalTone(isRejected, isDiscrepancy),
      summary: goodsReceivedNote?.finalisedAt ? (
        <GoodsReceivedNoteSummary
          selectedInboundProcess={process}
          totalReceivedItemsQuantity={totalReceived}
          isDiscrepancy={isDiscrepancy}
          discrepancyAmount={discrepancyAmount}
          isNegativeDiscrepancy={isNegativeDiscrepancy}
        />
      ) : undefined,
      view: goodsReceivedNote
        ? {
            label: 'View Goods Received Note',
            onView: views.onViewGoodsReceivedNote,
          }
        : undefined,
      action: actions.onSignOffGoodsReceivedNote
        ? {
            label: 'Sign Off Goods Received Note',
            onAct: actions.onSignOffGoodsReceivedNote,
            startIcon: <CheckIcon className='w-4 h-4' />,
          }
        : undefined,
    },
  ]);
}
